import type { DocNode } from '@atlaskit/adf-schema/doc';

import type { StreamAIEvent } from '../../editor-actions/types';

type StreamContent = Partial<Pick<StreamAIEvent, 'adfResponse' | 'markdown'>>;

/**
 * Markdown blocks are separated by one or more blank lines. The separator is
 * captured so it can be restored verbatim when blocks are rejoined, rather
 * than being normalised to a fixed number of newlines.
 */
const BLOCK_SEPARATOR = /((?:\r?\n){2,})/u;

const isDoc = (adfResponse: StreamAIEvent['adfResponse']): adfResponse is DocNode =>
	adfResponse?.type === 'doc' && Array.isArray((adfResponse as DocNode).content);

/** Splitting on a capturing separator interleaves blocks and separators. */
const splitBlocks = (markdown: string): string[] => markdown.split(BLOCK_SEPARATOR);

const countMarkdownBlocks = (parts: string[]): number => Math.ceil(parts.length / 2);

/**
 * Streamed responses are cumulative, and the trailing block is still being
 * written. A block is only known to be complete once a later one exists, so
 * the last is always withheld until the stream terminates.
 *
 * A response consisting of a single block therefore publishes nothing until
 * completion. That is inherent to block granularity: there is no earlier point
 * at which any whole block is known to be final.
 */
export const countCompleteBlocks = ({ adfResponse, markdown }: StreamContent): number => {
	if (isDoc(adfResponse)) {
		return Math.max(0, adfResponse.content.length - 1);
	}

	if (typeof markdown === 'string') {
		return Math.max(0, countMarkdownBlocks(splitBlocks(markdown)) - 1);
	}

	return 0;
};

/**
 * Narrows a cumulative response to its first `blockCount` complete blocks.
 *
 * Only the field that determined the count is returned. ADF and markdown do
 * not share a block boundary definition - a fenced code block or a spaced list
 * is one ADF node but several markdown blocks - so slicing both by a single
 * count could truncate one of them mid-block. Consumers resolve ADF ahead of
 * markdown, so the authoritative field is the one worth publishing.
 */
export const takeCompleteBlocks = (
	{ adfResponse, markdown }: StreamContent,
	blockCount: number,
): StreamContent => {
	if (isDoc(adfResponse)) {
		return { adfResponse: { ...adfResponse, content: adfResponse.content.slice(0, blockCount) } };
	}

	if (typeof markdown === 'string') {
		const parts = splitBlocks(markdown);
		// Each block occupies an even index, with its trailing separator next to
		// it, so N blocks span the first 2N-1 entries.
		return { markdown: parts.slice(0, Math.max(0, blockCount * 2 - 1)).join('') };
	}

	return {};
};
